import type { Lead } from '../../types';

export type FunilIaStageSlug = 'inicio' | 'em_negociacao_ia' | 'proposta_ia';

const STAGE_RANK: Record<FunilIaStageSlug, number> = {
  inicio: 0,
  em_negociacao_ia: 1,
  proposta_ia: 2,
};

function stageFromKanbanStatus(lead: Lead): FunilIaStageSlug {
  switch (lead.status) {
    case 'em_negociacao':
      return 'em_negociacao_ia';
    case 'proposta':
    case 'fechamento':
      return 'proposta_ia';
    default:
      return 'inicio';
  }
}

/**
 * Estágio visual do funil: combina `aiFunnelStage` (persistido) com o estado do Kanban.
 * Evita ficar preso em «inicio» quando o CRM já avançou (ex.: `em_negociacao` após preço).
 * No backend, `kind=proposta` só aplica `proposta_ia` em campanha `ai_funnel` após negociação no funil
 * (ou coluna compatível) — evita «info sobre curso» ir direto a Proposta por classificação errada da IA.
 */
export function resolveFunilIaStage(lead: Lead): FunilIaStageSlug {
  const fromStatus = stageFromKanbanStatus(lead);
  const explicit = lead.aiFunnelStage;
  if (!explicit) return fromStatus;
  const rExp = STAGE_RANK[explicit] ?? 0;
  const rStat = STAGE_RANK[fromStatus] ?? 0;
  return rStat > rExp ? fromStatus : explicit;
}

export function leadInitials(nome: string): string {
  const parts = nome
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Primeira letra do nome da campanha (ignora símbolos/números iniciais). */
export function campaignInitialLetter(nome: string): string {
  const s = nome.normalize('NFD').replace(/\p{M}/gu, '');
  for (const ch of s) {
    if (/\p{L}/u.test(ch)) return ch.toUpperCase();
  }
  const t = s.trim();
  if (t.length > 0) return t[0].toUpperCase();
  return '?';
}

export function groupLeadsByFunilStage(leads: Lead[]): Record<FunilIaStageSlug, Lead[]> {
  const empty: Record<FunilIaStageSlug, Lead[]> = {
    inicio: [],
    em_negociacao_ia: [],
    proposta_ia: [],
  };
  for (const lead of leads) {
    /** Handoff persistido: o lead só aparece no Kanban humano, não no Funil IA. */
    if (lead.aiFunnelHandoffAt) continue;
    const bin = lead.iaConversationContext?.handoffBin;
    /** Bins «finais» só na coluna de handoff; sem isto a bolinha ficava no funil com contagem no bin (ex.: opt-out). */
    if (bin === 'optout' || bin === 'toxic') continue;
    empty[resolveFunilIaStage(lead)].push(lead);
  }
  return empty;
}
